import { useState, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BusinessMap = ({ businesses, selectedCategory, onBusinessSelect }) => {
  const [map, setMap] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env?.VITE_GOOGLE_MAPS_API_KEY || ''
  });

  // Default center (Pasto, Colombia)
  const defaultCenter = useMemo(() => ({
    lat: 1.2136,
    lng: -77.2811
  }), []);

  // Map container style
  const mapContainerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '0.5rem'
  };

  // Map options
  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true
  };

  // Category colors for markers
  const categoryColors = {
    'Restaurantes': '#EF4444',
    'Moda y Accesorios': '#EC4899',
    'Tecnología': '#3B82F6',
    'Salud y Belleza': '#8B5CF6',
    'Hogar y Decoración': '#F59E0B',
    'Deportes y Fitness': '#10B981',
    'Educación': '#6366F1',
    'Entretenimiento': '#F97316',
    'Servicios Profesionales': '#14B8A6',
    'Automotriz': '#64748B'
  };

  // Filter businesses by category
  const filteredBusinesses = useMemo(() => {
    if (!selectedCategory) return businesses;
    return businesses?.filter(b => b?.category === selectedCategory);
  }, [businesses, selectedCategory]);

  // Get businesses with valid coordinates
  const businessesWithCoords = useMemo(() => {
    return filteredBusinesses?.filter(b => b?.latitude && b?.longitude);
  }, [filteredBusinesses]);

  const onLoad = useCallback((map) => {
    setMap(map);
    
    // Fit bounds to show all markers
    if (businessesWithCoords?.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      businessesWithCoords?.forEach(business => {
        bounds?.extend({
          lat: business?.latitude,
          lng: business?.longitude
        });
      });
      map?.fitBounds(bounds);
    }
  }, [businessesWithCoords]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Get user's current location
  const handleGetUserLocation = () => {
    if (navigator?.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          const location = {
            lat: position?.coords?.latitude,
            lng: position?.coords?.longitude
          };
          setUserLocation(location);
          if (map) {
            map?.panTo(location);
            map?.setZoom(14);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('No se pudo obtener tu ubicación. Por favor, verifica los permisos del navegador.');
        }
      );
    } else {
      alert('Tu navegador no soporta geolocalización.');
    }
  };

  // Open navigation to business
  const handleNavigate = (business) => {
    const destination = `${business?.latitude},${business?.longitude}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, '_blank');
  };

  // Handle marker click
  const handleMarkerClick = (business) => {
    setSelectedBusiness(business);
    if (onBusinessSelect) {
      onBusinessSelect(business);
    }
  };

  if (loadError) {
    return (
      <div className="bg-card rounded-lg p-8 text-center">
        <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Error al cargar el mapa</h3>
        <p className="text-sm text-muted-foreground">Por favor, verifica tu conexión a internet e intenta nuevamente.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-card rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-sm text-muted-foreground">Cargando mapa...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Map controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={handleGetUserLocation}
          iconName="Navigation"
          iconPosition="left"
          className="shadow-lg"
        >
          Mi ubicación
        </Button>
      </div>
      {/* Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={userLocation || defaultCenter}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
      >
        {/* User location marker */}
        {userLocation && (
          <Marker
            position={userLocation}
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE,
              scale: 8,
              fillColor: '#3B82F6',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2
            }}
          />
        )}

        {/* Business markers */}
        {businessesWithCoords?.map((business) => (
          <Marker
            key={business?.id}
            position={{
              lat: business?.latitude,
              lng: business?.longitude
            }}
            onClick={() => handleMarkerClick(business)}
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE,
              scale: 10,
              fillColor: categoryColors?.[business?.category] || '#6366F1',
              fillOpacity: 0.9,
              strokeColor: '#FFFFFF',
              strokeWeight: 2
            }}
          />
        ))}

        {/* Info window for selected business */}
        {selectedBusiness && (
          <InfoWindow
            position={{
              lat: selectedBusiness?.latitude,
              lng: selectedBusiness?.longitude
            }}
            onCloseClick={() => setSelectedBusiness(null)}
          >
            <div className="p-2 max-w-xs">
              <h3 className="font-bold text-base mb-1">{selectedBusiness?.name}</h3>
              <div className="flex items-center gap-1 mb-2">
                <Icon name="Tag" size={12} />
                <span className="text-xs text-gray-600">{selectedBusiness?.category}</span>
              </div>
              <div className="flex items-center gap-1 mb-2">
                <Icon name="MapPin" size={12} />
                <span className="text-xs text-gray-600">{selectedBusiness?.address}</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
                  {selectedBusiness?.discount}% OFF
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={12} className="text-yellow-500 fill-current" />
                  <span className="text-xs font-semibold">{selectedBusiness?.rating}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleNavigate(selectedBusiness)}
                  className="flex-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                >
                  <Icon name="Navigation" size={12} />
                  Cómo llegar
                </button>
                <button
                  onClick={() => {
                    const message = encodeURIComponent(`Hola ${selectedBusiness?.name}, vi tu negocio en BIZZNET`);
                    window.open(`https://wa.me/${selectedBusiness?.whatsapp}?text=${message}`, '_blank');
                  }}
                  className="flex-1 bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-1"
                >
                  <Icon name="MessageCircle" size={12} />
                  WhatsApp
                </button>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      {/* Legend */}
      <div className="mt-4 bg-card rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Map" size={16} />
          Leyenda de categorías
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(categoryColors)?.map(([category, color]) => (
            <div key={category} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-muted-foreground">{category}</span>
            </div>
          ))}
        </div>
      </div>
      {/* No businesses message */}
      {businessesWithCoords?.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
          <div className="bg-card rounded-lg p-6 text-center max-w-sm">
            <Icon name="MapPin" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No hay negocios en el mapa</h3>
            <p className="text-sm text-muted-foreground">
              {selectedCategory
                ? 'No hay negocios de esta categoría con ubicación registrada.' :'No hay negocios con ubicación registrada en este momento.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessMap;